import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from 'react-query';
import api from '../services/api.ts';

interface Settings {
  companyName: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  language: 'pt-BR' | 'en-US';
  currency: 'BRL' | 'USD' | 'EUR';
  theme: 'light' | 'dark' | 'system';
  notifications: boolean;
  emailNotifications: boolean;
}

interface SettingsContextType {
  settings: Settings | null;
  loading: boolean;
  error: string | null;
  refetchSettings: () => void;
  applyTheme: () => void;
}

const defaultSettings: Settings = {
  companyName: 'Gestão Patrimonial',
  logoUrl: undefined,
  primaryColor: '#3B82F6',
  secondaryColor: '#1E40AF',
  language: 'pt-BR',
  currency: 'BRL',
  theme: 'light',
  notifications: true,
  emailNotifications: false,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings | null>(null);

  const { data, isLoading, error, refetch } = useQuery(
    'app-settings',
    async () => {
      const response = await api.get('/settings');
      return response.data;
    },
    {
      onSuccess: (data) => {
        setSettings(data);
        applyThemeToDOM(data);
      },
      onError: (error) => {
        console.error('Erro ao carregar configurações:', error);
        // Use configurações padrão em caso de erro
        setSettings(defaultSettings);
        applyThemeToDOM(defaultSettings);
      },
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutos
    }
  );

  const applyThemeToDOM = (settingsData: Settings) => {
    const root = document.documentElement;
    root.style.setProperty('--danger', '#B91C1C');
    root.style.setProperty('--wl-primary', settingsData.primaryColor || '#3B82F6');
    root.style.setProperty('--wl-primary-hover', settingsData.secondaryColor || '#1E40AF');
    
    // Função para gerar variações de cor
    const generateColorVariations = (baseColor: string, prefix: string) => {
      // Converter hex para RGB
      const hex = baseColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Gerar variações de luminosidade
      const variations = {
        50: `rgb(${Math.min(255, r + 200)}, ${Math.min(255, g + 200)}, ${Math.min(255, b + 200)})`,
        100: `rgb(${Math.min(255, r + 150)}, ${Math.min(255, g + 150)}, ${Math.min(255, b + 150)})`,
        200: `rgb(${Math.min(255, r + 100)}, ${Math.min(255, g + 100)}, ${Math.min(255, b + 100)})`,
        300: `rgb(${Math.min(255, r + 50)}, ${Math.min(255, g + 50)}, ${Math.min(255, b + 50)})`,
        400: `rgb(${Math.min(255, r + 25)}, ${Math.min(255, g + 25)}, ${Math.min(255, b + 25)})`,
        500: baseColor,
        600: `rgb(${Math.max(0, r - 25)}, ${Math.max(0, g - 25)}, ${Math.max(0, b - 25)})`,
        700: `rgb(${Math.max(0, r - 50)}, ${Math.max(0, g - 50)}, ${Math.max(0, b - 50)})`,
        800: `rgb(${Math.max(0, r - 100)}, ${Math.max(0, g - 100)}, ${Math.max(0, b - 100)})`,
        900: `rgb(${Math.max(0, r - 150)}, ${Math.max(0, g - 150)}, ${Math.max(0, b - 150)})`,
      };
      
      // Aplicar todas as variações
      Object.entries(variations).forEach(([shade, color]) => {
        if (shade === '500') {
          root.style.setProperty(`--color-${prefix}`, color);
        } else {
          root.style.setProperty(`--color-${prefix}-${shade}`, color);
        }
      });
    };
    
    // Aplicar cores personalizadas com todas as variações
    generateColorVariations(settingsData.primaryColor, 'primary');
    generateColorVariations(settingsData.secondaryColor, 'secondary');
    
    // Aplicar título da empresa
    document.title = settingsData.companyName;
    
    // Aplicar favicon se existir
    if (settingsData.logoUrl) {
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      // Corrigir caminho da logo para favicon
      const logoUrl = settingsData.logoUrl.startsWith('/uploads/') 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${settingsData.logoUrl}`
        : settingsData.logoUrl;
      favicon.href = logoUrl;
    }
  };

  const applyTheme = () => {
    if (settings) {
      applyThemeToDOM(settings);
    }
  };

  useEffect(() => {
    if (settings) {
      applyThemeToDOM(settings);
    }
  }, [settings]);

  const value: SettingsContextType = {
    settings: settings || defaultSettings,
    loading: isLoading,
    error: error ? 'Erro ao carregar configurações' : null,
    refetchSettings: refetch,
    applyTheme,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;